import { ApiError } from "@/shared/api/http-client";

/**
 * Backend TOS error envelope. Every 4xx from
 * `wanderfit-api/src/tos/infrastructure/tos-error.mapper.ts` looks like
 *
 *     { errorCode: string, message: string, details?: Record<string,unknown> }
 *
 * `details` carries structured context the UI surfaces in copy
 * (e.g. `TOS_DOCUMENT_TYPE_IN_USE.details = { documents, requirements }`).
 */
interface TosErrorBody {
  readonly errorCode?: string;
  readonly message?: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

/**
 * Closed set of TOS error codes the BO knows how to render. Mirrors
 * `wanderfit-api/src/tos/domain/tos-errors.ts`. Every value here MUST
 * have a matching key under `compliance.errors.<key>` in both
 * `en/common.json` and `es/common.json` — the parity test guards that.
 */
export const TOS_ERROR_CODE_KEYS = {
  // Document types
  TOS_DOCUMENT_TYPE_NOT_FOUND: "compliance.errors.tosDocumentTypeNotFound",
  TOS_DOCUMENT_TYPE_KEY_EXISTS: "compliance.errors.tosDocumentTypeKeyExists",
  TOS_DOCUMENT_TYPE_SYSTEM_DELETE: "compliance.errors.tosDocumentTypeSystemDelete",
  TOS_DOCUMENT_TYPE_IN_USE: "compliance.errors.tosDocumentTypeInUse",
  // Locales
  LOCALE_NOT_FOUND: "compliance.errors.localeNotFound",
  LOCALE_CODE_EXISTS: "compliance.errors.localeCodeExists",
  LOCALE_IS_CANONICAL_CANNOT_DELETE: "compliance.errors.localeIsCanonicalCannotDelete",
  LOCALE_IN_USE: "compliance.errors.localeInUse",
  // Requirements
  TOS_ROLE_REQUIREMENT_NOT_FOUND: "compliance.errors.tosRoleRequirementNotFound",
  NO_ACTIVE_DOCUMENT_FOR_REQUIREMENT: "compliance.errors.noActiveDocumentForRequirement",
  // Documents + translations
  TOS_NOT_FOUND: "compliance.errors.tosNotFound",
  TOS_VERSION_CONFLICT: "compliance.errors.tosVersionConflict",
  TOS_MISSING_CANONICAL_TRANSLATION: "compliance.errors.tosMissingCanonicalTranslation",
  TOS_TRANSLATION_NOT_FOUND: "compliance.errors.tosTranslationNotFound",
  TOS_TRANSLATION_EXISTS: "compliance.errors.tosTranslationExists",
  TOS_ALREADY_PUBLISHED: "compliance.errors.tosAlreadyPublished",
  TOS_EFFECTIVE_FROM_BEFORE_ACTIVE: "compliance.errors.tosEffectiveFromBeforeActive",
  TOS_NO_FIELDS_PROVIDED: "compliance.errors.tosNoFieldsProvided",
  // Defensive — admins shouldn't hit these from BO endpoints, but they
  // appear in the same envelope; mapped so the fallback path never
  // surfaces a raw `Server: TOS_ROLE_NOT_SUPPORTED` toast.
  TOS_ROLE_NOT_SUPPORTED: "compliance.errors.tosRoleNotSupported",
  TOS_CONFIG_INCOMPLETE: "compliance.errors.tosConfigIncomplete",
} as const;

export type KnownTosErrorCode = keyof typeof TOS_ERROR_CODE_KEYS;

export interface ResolvedTosError {
  /** The i18n key the UI should render. */
  readonly i18nKey: string;
  /** The structured `details` payload, when present. */
  readonly details?: Readonly<Record<string, unknown>>;
  /** Raw backend error code for logging / testid suffixes. */
  readonly code?: string;
}

function isTosErrorBody(value: unknown): value is TosErrorBody {
  return typeof value === "object" && value !== null && "errorCode" in value;
}

/**
 * Best-effort lookup. Returns `null` if the error doesn't carry one of
 * the known TOS error codes — callers fall through to the shared
 * `resolveMutationErrorMessage` for generic 4xx + 5xx handling.
 */
export function resolveTosErrorKey(err: unknown): ResolvedTosError | null {
  if (!(err instanceof ApiError)) return null;
  if (!isTosErrorBody(err.body)) return null;
  const code = err.body.errorCode;
  if (typeof code !== "string") return null;
  const key = (TOS_ERROR_CODE_KEYS as Record<string, string>)[code];
  if (!key) return null;
  return { i18nKey: key, details: err.body.details, code };
}
